import { List, Row } from "antd"
import cx from "clsx"
import { StatsHeading } from "features/reports/components"
import Funnel from "features/reports/components/funnel/funnels"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { skipToken } from "@reduxjs/toolkit/query"
import {
  AIInsights,
  Card,
  ComponentState,
  DataTable,
  Icon,
  Number,
  SelectInput,
  Skeleton,
  Space,
  Text,
  Tooltip,
} from "components/common"
import { TitleDescription } from "components/custom"
import { DATE_TIME_FORMAT } from "constants"
import { convertToReadableString, joinBytItems, numberConvertor, numberFormat } from "utils"
import { dateTimeFunction } from "utils/date"
import { BarChart, DemoRadialBar, DonutChart, LineChart } from "components/charts"
import {
  getDonutBreakdownTooltipContent,
  getDonutColorResolver,
  getDonutData,
  getDonutTooltipFormatter,
  getLegacyReviewBreakdownOrder,
} from "./donutChartHelpers"
import ScoringBreakdown from "./scoringBreakdown"
import styles from "./index.module.less"

const colorMap = {
  revenue: ["#D9f1df", "#008725"],
  default: ["#839FF4", "#3F65DB"],
}
export const TabBasedMultiCharts = props => {
  const { t } = useTranslation()
  const {
    cardTittle,
    filters,
    useQuery,
    defaultActiveTab,
    tabList = [],
    className,
    dashboardType,
    onBarClickLink,
    loadType,
    extra = () => {},
    onHeaderScoreClick,
    compactHeader,
    colorSchemes: schemes = {},
  } = props
  const getScoreProgressColor =
    typeof schemes.getScoreProgressColor === "function" ? schemes.getScoreProgressColor : () => "#839FF4"
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState(null)
  const updatedQueryObj = useMemo(
    () => ({
      ...filters,
      load: activeTab?.loadType || loadType,
      dashboardType,
      ...(activeTab?.targetCategory && { targetCategory: activeTab?.targetCategory }),
    }),
    [filters, activeTab, dashboardType, loadType]
  )
  const shouldSkip = !updatedQueryObj?.load
  const queryArg = shouldSkip ? skipToken : updatedQueryObj
  const { data, isFetching, isError, error, refetch } = useQuery(queryArg)
  const appScreenSize = useSelector(state => state.app.configs.screenSize)
  const [tabExtraFilter, setTabExtraFilter] = useState()

  useEffect(() => {
    const newActiveTab = tabList?.[0] || defaultActiveTab
    if ((newActiveTab && !data?.tabList) || (data?.tabList && !activeTab)) {
      setActiveTab(newActiveTab)
      setTabExtraFilter(newActiveTab?.defaultFilter || defaultActiveTab?.defaultFilter)
    }
  }, [tabList, defaultActiveTab, data?.tabList])

  const getTooltipContent = hoverInfo => {
    return (
      hoverInfo && (
        <Space direction="vertical" size={12}>
          {Object.keys(hoverInfo)?.map((item, index) => {
            return (
              <Space className={cx("w-100")} align="center" style={{ justifyContent: "space-between" }} size={24}>
                <span key={index}>{item}: </span>
                {!isNaN(+hoverInfo[item]) ? (
                  <span className="bold">
                    <Number value={hoverInfo[item]} />
                  </span>
                ) : (
                  <span className="bold">{hoverInfo[item]}</span>
                )}
              </Space>
            )
          })}
        </Space>
      )
    )
  }

  const renderRowValue = (value, columnDef, dataItem) => {
    switch (columnDef?.type) {
      case "string":
        return <Text>{value}</Text>
      case "currency":
        return <Number value={value} isStringOnly tooltip />
      case "stringWithExtraContent":
        return (
          <TitleDescription
            title={<Text>{value}</Text>}
            gapSpace={4}
            description={joinBytItems(
              columnDef?.extraContentValueKeys?.map(item => dataItem[item]),
              <Icon icon="GoPrimitiveDot" size=".7em" color="#D6D6D6" />
            )}
          />
        )
      case "time":
        return (
          <Text>{`${dateTimeFunction({
            date: dataItem?.from,
            timeZone: true,
            format: DATE_TIME_FORMAT,
          }).slice(11, -3)} - ${dateTimeFunction({
            date: dataItem?.to,
            timeZone: true,
            format: DATE_TIME_FORMAT,
          }).slice(11, -3)} `}</Text>
        )
      default:
        return value
    }
  }
  const getTableData = useCallback(() => {
    return data?.dataObject?.map((e, index) => {
      const rowData = {}
      data?.columns.forEach(columnDef => {
        rowData[columnDef.dataIndex] = columnDef?.hover ? (
          <Tooltip title={getTooltipContent(e?.hoverInfo?.[columnDef?.hoverKey])}>
            {renderRowValue(e?.[columnDef.valueKey], columnDef, e)}
          </Tooltip>
        ) : (
          renderRowValue(e?.[columnDef.valueKey], columnDef, e)
        )
      })
      rowData.key = index
      return rowData
    })
  }, [data])

  const handleBarClick = () => {
    if (onBarClickLink) navigate(onBarClickLink)
  }

  const renderComponents = useMemo(() => {
    switch (activeTab?.componentType) {
      case "funnel":
        return <Funnel type={t("Clients")} data={data?.funnel} isFetching={isFetching} />
      case "radialBar":
        return (
          <DemoRadialBar
            tabExtraFilter={tabExtraFilter}
            data={data}
            loading={isFetching}
            disabled={isFetching}
            queryObj={updatedQueryObj}
          />
        )
      case "scoringBreakdown": {
        const scoringData = data?.[tabExtraFilter] ?? data?.[activeTab?.dataObject]
        const scoreLabel = activeTab?.scoreLabel ?? t("Avg AI Score")
        const isAvgScoreClickable = activeTab?.openDrawerOnAvgScoreClick && onHeaderScoreClick
        const { getStrokeColor, ...progressProps } = activeTab?.progressProps ?? {}
        const resolveStrokeColor = typeof getStrokeColor === "function" ? getStrokeColor : getScoreProgressColor
        return (
          <ScoringBreakdown
            scoringData={scoringData}
            isFetching={isFetching}
            scoreLabel={scoreLabel}
            isAvgScoreClickable={isAvgScoreClickable}
            onAvgScoreClick={() =>
              onHeaderScoreClick({
                activeTab,
                data,
                tabExtraFilter,
                queryObj: updatedQueryObj,
              })
            }
            progressProps={progressProps}
            resolveStrokeColor={resolveStrokeColor}
          />
        )
      }
      case "donutChart": {
        const filterData = data?.[tabExtraFilter]
        const angleField = activeTab?.angleField ?? "value"
        const colorFieldKey = activeTab?.colorField ?? "type"
        const donutData = getDonutData({ filterData, data, dataObject: activeTab?.dataObject })
        const donutCenter = filterData?.donutCenter ?? data?.donutCenter
        const donutColor = getDonutColorResolver({
          colorScheme: activeTab?.colorScheme,
          schemes,
          colorFieldKey,
        })
        const legacyBreakdownOrder = getLegacyReviewBreakdownOrder({ activeTab, t })
        const breakdownOrder = activeTab?.breakdownOrder ?? legacyBreakdownOrder
        const defaultTooltip = {
          formatter: getDonutTooltipFormatter({
            angleField,
            colorField: colorFieldKey,
            useCompactBreakdown: Boolean(breakdownOrder),
          }),
          ...(breakdownOrder && {
            customContent: getDonutBreakdownTooltipContent({
              colorFieldKey,
              breakdownOrder,
            }),
          }),
        }
        const tooltipConfig = {
          ...defaultTooltip,
          ...(activeTab?.tooltip ?? {}),
        }
        return (
          <DonutChart
            data={donutData}
            angleField={angleField}
            colorField={colorFieldKey}
            radius={activeTab?.radius ?? schemes?.donutChart?.radius ?? 0.8}
            innerRadius={activeTab?.innerRadius ?? schemes?.donutChart?.innerRadius ?? 0.7}
            color={donutColor}
            statistic={
              donutCenter
                ? {
                    title: false,
                    content: {
                      content: `${donutCenter.count}`,
                      style: { whiteSpace: "pre-wrap", textAlign: "center", fontSize: 20 },
                    },
                  }
                : null
            }
            tooltip={tooltipConfig}
            legend={{
              position: "bottom",
              marker: { symbol: "circle", style: { width: 12 } },
              flipPage: donutData.length > 5 || (appScreenSize?.width <= 1450 && donutData.length > 3),
            }}
            loading={isFetching}
            disabled={isFetching}
            height={320}
          />
        )
      }
      case "barChart": {
        const barFilterData = data?.[tabExtraFilter]
        const barData = Array.isArray(barFilterData)
          ? barFilterData
          : (barFilterData?.[activeTab?.dataObject] ?? barFilterData) ?? data?.[activeTab?.dataObject] ?? []
        return (
          <BarChart
            data={barData}
            xField={activeTab?.xField}
            yField={activeTab?.yField}
            xAxis={{ label: { autoHide: false, style: { fontSize: appScreenSize?.width <= 1450 ? 9 : 12 } } }}
            seriesField="type"
            maxColumnWidth={20}
            colorField="type"
            isGroup={activeTab?.colorScheme === "reviewsLead"}
            tooltip={{
              formatter: datum => {
                const convertedValue = numberFormat(datum[activeTab?.yField], "en-US", 2)
                return { name: datum.type, value: convertedValue }
              },
            }}
            color={({ type }) =>
              activeTab?.colorScheme === "reviewsLead"
                ? schemes?.reviewsLeadBar?.[type] ?? "#839FF4"
                : type === "Repeat" || type === "Probable Revenue"
                  ? colorMap[activeTab?.colorScheme || "default"][0]
                  : colorMap[activeTab?.colorScheme || "default"][1]
            }
            yAxis={{ label: { formatter: v => numberConvertor(v, "intl", 0) } }}
            legend={{
              marker: { symbol: "circle", style: { width: 12 } },
              position: "bottom",
              ...(activeTab?.unselectedLegends?.length && {
                selected: Object.fromEntries(activeTab?.unselectedLegends?.map(key => [key, false])),
              }),
            }}
            loading={isFetching}
            disabled={isFetching}
            height={320}
            isStacked={activeTab?.colorScheme === "reviewsLead" ? false : activeTab?.isStacked}
            handleBarClick={activeTab?.isClickable && handleBarClick}
          />
        )
      }
      case "lineChart":
        return (
          <LineChart
            data={data?.[tabExtraFilter] ?? data?.[activeTab?.dataObject] ?? []}
            xField={activeTab?.xField}
            yField={activeTab?.yField}
            seriesField={activeTab?.seriesField}
            xAxis={{
              label: {
                autoHide: false,
                style: { fontSize: appScreenSize?.width <= 1450 ? 9 : 12 },
                ...(Array.isArray(data?.lineChartAxisDates) &&
                data.lineChartAxisDates.length && {
                  formatter: v => (data.lineChartAxisDates.includes(v) ? v : ""),
                }),
              },
            }}
            yAxis={{
              min: activeTab?.lineColorScheme === "scoringTrend" ? 0 : null,
              max: activeTab?.lineColorScheme === "scoringTrend" ? 100 : null,
              label: { formatter: v => (activeTab?.lineColorScheme === "scoringTrend" ? `${v}%` : numberConvertor(v, "intl", 2)) },
            }}
            tooltip={{
              formatter: datum => {
                const raw = datum[activeTab?.yField]
                const convertedValue =
                  activeTab?.lineColorScheme === "scoringTrend" ? `${Math.round(raw)}%` : numberFormat(raw, "en-US", 2)
                const name = activeTab?.seriesField ? datum[activeTab.seriesField] : activeTab?.yField
                return { name, value: convertedValue }
              },
            }}
            legend={
              activeTab?.seriesField
                ? {
                    position: "bottom",
                    flipPage: false,
                    offsetY: 12,
                    marker: {
                      symbol: (x, y, r) => [
                        ["M", x - r, y],
                        ["L", x + r, y],
                      ],
                      style: { lineWidth: 3 },
                    },
                    ...(activeTab?.unselectedLegends?.length && {
                      selected: Object.fromEntries(activeTab?.unselectedLegends?.map(key => [key, false])),
                    }),
                  }
                : false
            }
            color={
              activeTab?.seriesField
                ? datum =>
                    (activeTab?.lineColorScheme === "scoringTrend"
                      ? schemes?.scoringTrendLine?.[datum[activeTab.seriesField]]
                      : schemes?.meetingAttendanceLine?.[datum[activeTab.seriesField]]) ??
                    colorMap[activeTab?.colorScheme || "default"]?.[1] ??
                    "#839FF4"
                : null
            }
            loading={isFetching}
            disabled={isFetching}
            height={320}
          />
        )
      case "table":
        return (
          <DataTable
            tableLayout="auto"
            columns={data?.columns}
            data={getTableData() || []}
            loading={isFetching}
            error={isError}
            onErrorRetry={refetch}
            offsetHeader={110}
            scroll={{ y: 300 }}
          />
        )
      case "aiInsights":
        return <AIInsights isFetching={isFetching} data={data} />
      case "listItems":
        return isFetching ? (
          <div className="p-16">
            <Skeleton type="antparagraph" active paragraph={{ rows: 8, width: ["80%", "60%", "70%", "50%"] }} />
          </div>
        ) : (
          <List
            size="small"
            dataSource={data?.agenda}
            className={"p-16"}
            style={{ "--bg-color": data?.backgroundColor }}
            renderItem={item => (
              <List.Item style={{ paddingBlock: 5 }}>
                <Text type={item?.labelType} style={{ opacity: item?.value === 0 ? 0.6 : 1 }}>
                  <Icon icon={"BsCheck2Circle"} size={14} style={{ marginInlineEnd: 6 }} />
                  {item.label}
                </Text>
                {item?.value === 0 ? (
                  <Icon icon={"SvgIcParty"} size={20} />
                ) : (
                  <Tooltip title={getTooltipContent(item?.hoverInfo)}>
                    <Text>{item.value}</Text>
                  </Tooltip>
                )}
              </List.Item>
            )}
          />
        )
      default:
        return null
    }
  }, [activeTab, isFetching, data, tabExtraFilter])
  return !!error ? (
    <Card
      title={cardTittle}
      className={className}
      tabList={tabList}
      activeTabKey={activeTab?.key}
      onTabChange={key => setActiveTab((tabList?.length ? tabList : data?.tabList).find(item => item.key === key))}
    >
      <ComponentState
        description={error.message}
        status={error.status}
        btnLoading={isFetching}
        onClick={() => refetch()}
      />
    </Card>
  ) : (
    <Card
      title={cardTittle}
      extra={extra({ setTabExtraFilter: setTabExtraFilter, activeTab: activeTab, tabExtraFilter: tabExtraFilter })}
      className={cx(className, compactHeader && styles.compactHeader)}
      tabList={tabList?.length ? tabList : data?.tabList}
      activeTabKey={activeTab?.key}
      onTabChange={key => setActiveTab((tabList?.length ? tabList : data?.tabList).find(item => item.key === key))}
      tabBarExtraContent={
        activeTab?.extraTitleType === "filter" ? (
          <SelectInput
            value={tabExtraFilter ?? activeTab?.defaultFilter ?? activeTab?.filterData?.[0]?.value}
            options={activeTab?.filterData}
            style={{ width: "150px", marginBottom: "5px" }}
            onChange={value => setTabExtraFilter(value)}
            defaultValue={activeTab?.defaultFilter ?? activeTab?.filterData?.[0]?.value}
          />
        ) : null
      }
      headStyle={{
        borderBottom: 0,
        ...(compactHeader && { paddingBottom: 0, marginBottom: 0 }),
      }}
      bodyStyle={{ minHeight: 200, direction: "ltr" }}
    >
      {(!!activeTab?.tabExtraHeader || activeTab?.componentType === "donutChart") ? (
        <Row justify="space-between" align="middle" style={{ marginBottom: "10px", minHeight: 22 }}>
          {activeTab?.headerTitleValueOnRight ? (
            <>
              <div />
              {data?.tabExtraHeader?.[tabExtraFilter]?.title || data?.tabExtraHeader?.[activeTab?.dataObject]?.title ? (
                isFetching ? (
                  <Skeleton />
                ) : (
                  <span
                    className={onHeaderScoreClick && activeTab?.openDrawerOnScoreClick ? "pointer" : null}
                    onClick={
                      onHeaderScoreClick && activeTab?.openDrawerOnScoreClick
                        ? () =>
                            onHeaderScoreClick({
                              activeTab,
                              data,
                              tabExtraFilter,
                              queryObj: updatedQueryObj,
                            })
                        : null
                    }
                  >
                    <Text>
                      {activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                        ? data?.tabExtraHeader?.[tabExtraFilter]?.title
                        : data?.tabExtraHeader?.[activeTab?.dataObject]?.title}
                      :{" "}
                      {activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                        ? data?.tabExtraHeader?.[tabExtraFilter]?.value ?? 0
                        : data?.tabExtraHeader?.[activeTab?.dataObject]?.value ?? 0}
                    </Text>
                  </span>
                )
              ) : (
                <div />
              )}
            </>
          ) : data?.tabExtraHeader?.[tabExtraFilter]?.title || data?.tabExtraHeader?.[activeTab?.dataObject]?.title ? (
            <StatsHeading
              type="title"
              title={
                activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                  ? data?.tabExtraHeader?.[tabExtraFilter]?.title
                  : data?.tabExtraHeader?.[activeTab?.dataObject]?.title
              }
              value={
                activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                  ? data?.tabExtraHeader?.[tabExtraFilter]?.value || 0
                  : data?.tabExtraHeader?.[activeTab?.dataObject]?.value || 0
              }
              isFetching={isFetching}
              valueType={activeTab?.valueType}
            />
          ) : (
            <div />
          )}
          {!activeTab?.headerTitleValueOnRight && activeTab?.extraTitle && (
            <StatsHeading
              type="extra"
              title={activeTab?.extraTitle}
              value={data?.[activeTab?.loadType]?.[activeTab?.subTitleValueIdentifier] || 0}
              percentage={activeTab?.extraValueWithPercentSign && "%"}
              isFetching={isFetching}
              valueType={activeTab?.valueType}
            />
          )}
          {!activeTab?.headerTitleValueOnRight &&
            activeTab?.extraTitleType === "filter" &&
            !!tabExtraFilter &&
            !!data?.tabExtraHeader?.[tabExtraFilter]?.subValue && (
              <StatsHeading
                title={
                  activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                    ? data?.tabExtraHeader?.[tabExtraFilter]?.subTitle
                    : data?.tabExtraHeader?.[activeTab?.dataObject]?.subTitle
                }
                value={
                  activeTab?.extraTitleType === "filter" && !!tabExtraFilter
                    ? data?.tabExtraHeader?.[tabExtraFilter]?.subValue || 0
                    : data?.tabExtraHeader?.[activeTab?.dataObject]?.subValue || 0
                }
                percentage={
                  data?.tabExtraHeader?.[activeTab?.dataObject]?.percentage > 0 && (
                    <Number
                      value={data?.tabExtraHeader?.[activeTab?.dataObject]?.percentage}
                      isStringOnly
                      wrapValue={v => `(${v} %)`}
                    />
                  )
                }
                type="extra"
                isFetching={isFetching}
                valueType={activeTab?.valueType}
              />
            )}
        </Row>
      ) : null}
      {renderComponents}
    </Card>
  )
}
